import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight } from "lucide-react";
import { absoluteUrl } from "../lib/adminApi";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fisher-Yates shuffle (returns new array)
const shuffle = (arr) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

export default function ClientsMarquee() {
  const [clients, setClients] = useState([]);

  useEffect(() => {
    axios
      .get(`${API}/clients`)
      .then((r) => setClients(shuffle(r.data.clients || [])))
      .catch(() => {});
  }, []);

  if (!clients.length) return null;

  // Two duplicates for seamless infinite scroll
  const loop = [...clients, ...clients, ...clients];
  // If we have fewer than 4 clients, slow down the scroll proportionally
  const duration = Math.max(28, clients.length * 4.5);

  return (
    <section
      className="relative overflow-hidden bg-gradient-to-b from-slate-50 to-white border-y border-slate-100"
      data-testid="clients-marquee"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <div className="text-center mb-8 md:mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#E8F4F8] text-[#1C6B8A] text-[11px] font-semibold uppercase tracking-widest">
            <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
            Trusted by
          </div>
          <h2 className="font-display text-2xl md:text-4xl font-bold text-slate-900 mt-3 tracking-tight">
            Brands & businesses across <span className="text-[#1C6B8A]">Nagpur</span>
          </h2>
          <p className="text-slate-500 text-sm md:text-base mt-2">
            Hospitals · Showrooms · Offices · Warehouses · Homes
          </p>
        </div>

        <div
          className="relative"
          style={{
            maskImage:
              "linear-gradient(to right, transparent, black 6%, black 94%, transparent)",
            WebkitMaskImage:
              "linear-gradient(to right, transparent, black 6%, black 94%, transparent)",
          }}
        >
          <div
            className="flex items-stretch gap-4 md:gap-5 py-2"
            style={{
              animation: `clients-scroll ${duration}s linear infinite`,
              width: "max-content",
            }}
          >
            {loop.map((c, i) => (
              <Link
                key={`${c.id}-${i}`}
                to="/clients"
                data-testid={`client-logo-${c.id}-${i}`}
                className="group shrink-0 w-44 md:w-52 h-24 md:h-28 bg-white rounded-2xl border border-slate-200 px-5 py-3 flex items-center justify-center shadow-sm hover:shadow-lg hover:-translate-y-1 hover:border-[#1C6B8A]/40 transition-all duration-300"
                title={c.name}
              >
                <img
                  src={absoluteUrl(c.logo_url)}
                  alt={c.name}
                  className="max-h-full max-w-full object-contain"
                  loading="lazy"
                />
              </Link>
            ))}
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6 mt-8 text-xs text-slate-500 font-medium">
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
              10,000+ Happy Clients
            </div>
            <div className="hidden sm:flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-[#1C6B8A]" />
              12,000+ Projects
            </div>
            <div className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
              4.9 ★ on Google
            </div>
          </div>
          <Link
            to="/clients"
            data-testid="clients-view-all"
            className="inline-flex items-center gap-1.5 text-[#1C6B8A] hover:text-[#0f4a63] font-semibold transition-colors"
          >
            View all clients <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>

      <style>{`
        @keyframes clients-scroll {
          0% { transform: translateX(0); }
          100% { transform: translateX(-33.333%); }
        }
      `}</style>
    </section>
  );
}
